/**
 * The Products tab of a project, drawn as the pipeline it is.
 *
 * THE SOURCE PRODUCT'S PROJECT PAGE, seen 2026-10-27: a lane per product -- quote request ->
 * quote response -> build plan -- with "New quote request" filing a demand under that product,
 * and the chains whose request names no product listed below as records outside any pipeline.
 * Until then the Products tab here was a flat grid, the same as the other four kinds, which
 * answered "what is filed" and not "where does each demand stand".
 *
 * THE CHAIN IS READ ONCE PER REQUEST. A quote request's single read carries the offers drafted
 * against it and, under each offer, the plans drafted from it (Get Quote Request); the project's
 * member list says which requests are filed here. So the board costs one list and one get per
 * request, and nothing per offer or plan. Offers and plans filed in the project that no request
 * chain reaches are listed too, rather than hidden: a membership is a fact about the folder.
 *
 * A PRODUCT IS NOT AN ITEM. The lane a request sits in is decided by ProductObjectUSID on the
 * request -- the development subject -- which is why the dialog's product list is the PROJECT'S
 * products and not the item catalogue.
 *
 * NOTHING HERE IS AN AGENT. The source page carries "Quinn - prepare quote response" and "Clara -
 * draft contract" on every card; that is its fenced drafting layer, which this estate does not
 * have, and the buttons are not imitated.
 */
import ObjectIdLink from "./ObjectIdLink";
import React, {useEffect, useMemo, useState} from "react";
import {Box, Button, Paper, Typography} from "@mui/material";

import {Blue3, Grey2, Grey6, Grey7, Grey8, Grey17} from "../../theme/styles";

export type PipelineMember = {
    MemberType: string; MemberUSID: string; visible?: boolean;
    ObjectID?: string | null; Name?: string | null; Status?: string | null;
    RevisionUSID?: string | null;
};

type Offer = {
    PreparedQuoteRevisionUSID: string; ObjectUSID?: string | null; ObjectID?: string | null;
    Status?: string | null; Currency?: string | null; ValidUntil?: string | null;
    ListOfBuildPlan?: {BuildPlan?: Plan[] | Plan};
};
type Plan = {
    BuildPlanRevisionUSID?: string | null; ObjectUSID?: string | null; ObjectID?: string | null;
    Status?: string | null; Quantity?: string | null; NeedByDate?: string | null;
};
type Chain = {
    request: PipelineMember;
    satellite: {USID: string; Subject?: string | null; RequestedProduct?: string | null;
                TriageStatus?: string | null; Status?: string | null;
                ProductObjectUSID?: string | null; Quantity?: string | null;
                UnitOfMeasure?: string | null} | null;
    offers: Offer[];
    state: "loading" | "ready" | "failed";
};

type ProductDetail = {ProductType?: string | null; Description?: string | null} | null;

type Props = {
    api: any;
    products: PipelineMember[];
    requests: PipelineMember[];
    responses: PipelineMember[];
    plans: PipelineMember[];
    /** The folder still takes records, so the create doors are offered. */
    takesRecords: boolean;
    busy?: boolean;
    /** Open the quote request dialog, filed under this product (or none). */
    onNewRequest: (productObjectUSID: string | null) => void;
    onNewProduct: () => void;
};

const listOf = <T,>(held: T[] | T | undefined | null): T[] =>
    Array.isArray(held) ? held : (held ? [held] : []);

const TRIAGE_WORD: Record<string, string> = {
    RECEIVED: "received", QUOTED: "quoted", DECLINED: "declined", WITHDRAWN: "withdrawn",
    CLOSED: "closed",
};

function Chip({text, tone}: {text: string; tone?: "open" | "done" | "quiet"}) {
    const colour = tone === "done" ? "#2e7d32" : tone === "open" ? "#8a6d00" : Grey7;
    const background = tone === "done" ? "#e8f5e9" : tone === "open" ? "#fff8e1" : "#f3f4f6";
    return <Box component="span" sx={{fontSize: 10.5, color: colour, background,
                                     borderRadius: "10px", padding: "2px 8px",
                                     textTransform: "lowercase", whiteSpace: "nowrap"}}>
        {text}
    </Box>;
}

function Card({children, muted}: {children: React.ReactNode; muted?: boolean}) {
    return <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "10px 12px",
                     background: muted ? "#fafafa" : "white", minHeight: 64,
                     borderStyle: muted ? "dashed" : "solid"}}>{children}</Box>;
}

/**
 * A record's number, linking to its page. THE LINK NAMES THE REVISION: /object/{usid} is the
 * server's link shape and the server names a revision, so the resolver reads
 * object_revisions?USID= -- the revision's key. The chain read carries each record's revision
 * for exactly this, and the member rows carry theirs from the resolve.
 */
function RecordLine({id, name, revisionUSID}:
                    {id?: string | null; name?: string | null; revisionUSID?: string | null}) {
    // THE IDENTIFIER OPENS THE DRAWER AND THE ICON A NEW WINDOW -- see ObjectIdLink (Ed, 2026-10-30).
    return <Box sx={{display: "flex", alignItems: "center", gap: "8px", minWidth: 0}}>
        <ObjectIdLink id={id} revisionUSID={revisionUSID} size={12.5}/>
        <Typography sx={{fontSize: 12.5, color: Grey8, overflow: "hidden",
                         textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{name ?? ""}</Typography>
    </Box>;
}

/** One chain: the request, its offers stacked, each offer's plans beside it. */
function ChainRow({chain}: {chain: Chain}) {
    const {request, satellite, offers} = chain;
    const triage = String(satellite?.TriageStatus ?? satellite?.Status ?? "").toUpperCase();
    const subject = satellite?.Subject || satellite?.RequestedProduct || request.Name || "";
    const quantity = satellite?.Quantity
        ? `${satellite.Quantity}${satellite.UnitOfMeasure ? ` ${satellite.UnitOfMeasure}` : ""}`
        : null;
    const rows = offers.length ? offers : [null];
    return (
        <Box sx={{display: "grid", gridTemplateColumns: "1.1fr 1fr 1fr", gap: "12px",
                  padding: "10px 0", borderBottom: `1px solid ${Grey2}`}}>
            <Card>
                <Box sx={{display: "flex", justifyContent: "space-between", gap: "8px"}}>
                    <RecordLine id={request.ObjectID} name="" revisionUSID={request.RevisionUSID}/>
                    {triage && <Chip text={TRIAGE_WORD[triage] ?? triage.toLowerCase()}
                                     tone={triage === "RECEIVED" ? "open"
                                         : triage === "QUOTED" ? "done" : "quiet"}/>}
                </Box>
                <Typography sx={{fontSize: 12.5, color: Grey17, paddingTop: "4px"}}>
                    {subject || "—"}
                </Typography>
                {quantity && <Typography sx={{fontSize: 11.5, color: Grey7}}>{quantity}</Typography>}
                {chain.state === "failed" && (
                    <Typography sx={{fontSize: 11.5, color: Grey7, paddingTop: "4px"}}>
                        The request's chain could not be read.
                    </Typography>
                )}
            </Card>
            <Box sx={{display: "flex", flexDirection: "column", gap: "8px"}}>
                {rows.map((offer, index) => offer ? (
                    <Card key={offer.PreparedQuoteRevisionUSID}>
                        <Box sx={{display: "flex", justifyContent: "space-between", gap: "8px"}}>
                            <RecordLine id={offer.ObjectID} name=""
                                        revisionUSID={offer.PreparedQuoteRevisionUSID}/>
                            {offer.Status && <Chip text={offer.Status}
                                                   tone={String(offer.Status).toUpperCase() === "DRAFT"
                                                       ? "open" : "done"}/>}
                        </Box>
                        <Typography sx={{fontSize: 11.5, color: Grey7, paddingTop: "4px"}}>
                            {offer.Currency ? `${offer.Currency}` : "Quote response"}
                            {offer.ValidUntil ? ` · valid until ${String(offer.ValidUntil).slice(0, 10)}` : ""}
                        </Typography>
                    </Card>
                ) : (
                    <Card key={`none-${index}`} muted>
                        <Typography sx={{fontSize: 12, color: Grey6}}>
                            {chain.state === "loading" ? "Reading…" : "No quote response yet"}
                        </Typography>
                    </Card>
                ))}
            </Box>
            <Box sx={{display: "flex", flexDirection: "column", gap: "8px"}}>
                {rows.map((offer, index) => {
                    const plans = offer ? listOf<Plan>(offer.ListOfBuildPlan?.BuildPlan) : [];
                    if (!plans.length) {
                        return <Card key={`noplan-${index}`} muted>
                            <Typography sx={{fontSize: 12, color: Grey6}}>
                                {offer ? "No build plan yet" : "—"}
                            </Typography>
                        </Card>;
                    }
                    return <Box key={`plans-${index}`}
                                sx={{display: "flex", flexDirection: "column", gap: "8px"}}>
                        {plans.map(plan => (
                            <Card key={plan.BuildPlanRevisionUSID ?? plan.ObjectID ?? index}>
                                <Box sx={{display: "flex", justifyContent: "space-between",
                                          gap: "8px"}}>
                                    <RecordLine id={plan.ObjectID} name=""
                                                revisionUSID={plan.BuildPlanRevisionUSID}/>
                                    {plan.Status && <Chip text={plan.Status}
                                        tone={String(plan.Status).toUpperCase() === "DRAFT"
                                            ? "open" : "done"}/>}
                                </Box>
                                <Typography sx={{fontSize: 11.5, color: Grey7, paddingTop: "4px"}}>
                                    {plan.Quantity ? `Build ${plan.Quantity}` : "Build plan"}
                                    {plan.NeedByDate ? ` · by ${String(plan.NeedByDate).slice(0, 10)}` : ""}
                                </Typography>
                            </Card>
                        ))}
                    </Box>;
                })}
            </Box>
        </Box>
    );
}

export default function ProductPipeline({api, products, requests, responses, plans, takesRecords,
                                         busy, onNewRequest, onNewProduct}: Props) {
    const [selected, setSelected] = useState<string | null>(null);
    const [details, setDetails] = useState<Record<string, ProductDetail>>({});
    const [chains, setChains] = useState<Record<string, Chain>>({});

    // THE FIRST PRODUCT IS THE LANE SHOWN, unless somebody chose another. Keyed on the list's
    // content so a product filed later does not silently steal the selection.
    useEffect(() => {
        if (!products.length) { setSelected(null); return; }
        if (!selected || !products.some(one => one.MemberUSID === selected)) {
            setSelected(products[0].MemberUSID);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products.map(one => one.MemberUSID).join("|")]);

    // The product's own fields, for the card: type and what it is for. A PRODUCT IS AN ITEM OF
    // TYPE PRT- (2026-10-29) and those are two answers on its Product form, read once per product
    // from the form input the record carries -- there is no product read any more.
    useEffect(() => {
        let live = true;
        products.filter(one => one.visible && one.RevisionUSID && !(one.MemberUSID in details)).forEach(product => {
            api.get("t1/v1/object_form_input", {params: {ObjectRevisionUSID: product.RevisionUSID}})
                .then((answer: any) => {
                    if (!live) return;
                    const values = answer?.data?.PlatformMessage?.Values ?? answer?.data?.Values ?? null;
                    const held = values ? {ProductType: values.productType ?? null,
                                           Description: values.targetIndication ?? values.performanceClaim ?? null} : null;
                    setDetails(prior => ({...prior, [product.MemberUSID]: held}));
                })
                .catch(() => { if (live) setDetails(prior => ({...prior, [product.MemberUSID]: null})); });
        });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products.map(one => one.MemberUSID).join("|"), api]);

    // THE CHAINS. The list finds the satellite behind the logical object; the get carries the
    // offers and their plans. Two reads per request, none per offer or plan.
    useEffect(() => {
        let live = true;
        requests.forEach(request => {
            if (chains[request.MemberUSID]?.state === "ready") return;
            setChains(prior => ({...prior, [request.MemberUSID]:
                {request, satellite: null, offers: [], state: "loading"}}));
            api.get("t1/v1/quote_requests", {params: {ObjectUSID: request.MemberUSID},
                                             headers: {"idoc-response": "v1.QuoteRequest-001"}})
                .then((listed: any) => {
                    const rows = listOf<any>(listed?.data?.PlatformMessage?.ListOfQuoteRequest?.QuoteRequest);
                    const satellite = rows[0];
                    if (!satellite?.USID) return {satellite: null, offers: []};
                    return api.get("t1/v1/quote_requests", {params: {USID: satellite.USID},
                                                            headers: {"idoc-response": "v1.QuoteRequest-001"}})
                        .then((got: any) => {
                            const one = got?.data?.PlatformMessage?.QuoteRequest ?? satellite;
                            return {satellite: one,
                                    offers: listOf<Offer>(one?.ListOfPreparedQuote?.PreparedQuote)};
                        });
                })
                .then(({satellite, offers}: {satellite: any; offers: Offer[]}) => {
                    if (!live) return;
                    setChains(prior => ({...prior, [request.MemberUSID]:
                        {request, satellite, offers, state: "ready"}}));
                })
                .catch(() => {
                    if (!live) return;
                    setChains(prior => ({...prior, [request.MemberUSID]:
                        {request, satellite: null, offers: [], state: "failed"}}));
                });
        });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requests.map(one => one.MemberUSID).join("|"), api]);

    // THE LIVE MEMBER, NOT THE ONE THE CHAIN WAS READ WITH. The chain is read once per request
    // and keeps the member it started from, which at that moment has no number yet -- the
    // resolve that supplies ObjectID and the revision runs in parallel. Re-reading the chain for
    // that would be a second round of reads for a field the parent already has; taking the
    // parent's current row is free. The first lane drawn showed "--" for a request that had a
    // number for exactly this reason.
    const chainList = useMemo(() => requests
        .map(one => chains[one.MemberUSID] && {...chains[one.MemberUSID], request: one})
        .filter(Boolean) as Chain[], [requests, chains]);
    const laneOf = (chain: Chain) => chain.satellite?.ProductObjectUSID ?? null;
    const inLane = chainList.filter(chain => selected && laneOf(chain) === selected);
    const outside = chainList.filter(chain => !laneOf(chain)
        || !products.some(product => product.MemberUSID === laneOf(chain)));

    // OFFERS AND PLANS NO CHAIN REACHES, so a filed record is never hidden by the shape.
    const reachedOffers = new Set(chainList.flatMap(chain => chain.offers.map(one => one.ObjectUSID)));
    const reachedPlans = new Set(chainList.flatMap(chain => chain.offers.flatMap(
        one => listOf<Plan>(one.ListOfBuildPlan?.BuildPlan).map(plan => plan.ObjectUSID))));
    const loose = [...responses.filter(one => !reachedOffers.has(one.MemberUSID)),
                   ...plans.filter(one => !reachedPlans.has(one.MemberUSID))];
    const stillLoading = chainList.some(chain => chain.state === "loading");

    const countsFor = (productUSID: string) => {
        const mine = chainList.filter(chain => laneOf(chain) === productUSID);
        const offers = mine.flatMap(chain => chain.offers);
        const made = offers.flatMap(one => listOf<Plan>(one.ListOfBuildPlan?.BuildPlan));
        return {requests: mine.length, offers: offers.length, plans: made.length};
    };

    const current = products.find(one => one.MemberUSID === selected) ?? null;

    return (
        <Box sx={{display: "grid", gridTemplateColumns: "250px 1fr", gap: "20px",
                  alignItems: "start"}}>
            {/* THE PRODUCTS, as cards. Clicking one shows its lane. */}
            <Box>
                {/* THE BUTTON IS THE COLUMN'S HEADING. The tab strip above already says
                    "Products (1)"; a second count here said it twice, and the button beside it
                    sat in half the width. Ed, 2026-10-27: let it span the space it is in. */}
                <Button fullWidth size="small" variant="outlined" disabled={busy || !takesRecords}
                        sx={{textTransform: "none", marginBottom: "10px"}} onClick={onNewProduct}>
                    New product
                </Button>
                {products.length === 0 && (
                    <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>
                        No products are filed in this project. A product is the subject a demand is
                        for; file one and the demands can be filed under it.
                    </Typography>
                )}
                <Box sx={{display: "flex", flexDirection: "column", gap: "10px"}}>
                    {products.map(product => {
                        const detail = details[product.MemberUSID];
                        const counts = countsFor(product.MemberUSID);
                        const active = product.MemberUSID === selected;
                        return <Box key={product.MemberUSID}
                                    onClick={() => setSelected(product.MemberUSID)}
                                    sx={{border: `1px solid ${active ? Blue3 : Grey2}`,
                                         borderRadius: "8px", padding: "10px 12px",
                                         cursor: "pointer",
                                         boxShadow: active ? `0 0 0 2px ${Blue3}22` : "none"}}>
                            <Typography sx={{fontSize: 13, fontWeight: 700, color: Grey17}}>
                                {product.visible === false ? "Filed here, and not visible to you"
                                    : (product.Name ?? "—")}
                            </Typography>
                            <ObjectIdLink id={product.ObjectID} revisionUSID={product.visible === false ? null : product.RevisionUSID}
                                          size={11.5} weight={600}/>
                            {detail?.ProductType && (
                                <Box sx={{paddingTop: "6px"}}><Chip text={detail.ProductType} tone="quiet"/></Box>
                            )}
                            {detail?.Description && (
                                <Typography sx={{fontSize: 11.5, color: Grey8, paddingTop: "6px",
                                                 display: "-webkit-box", WebkitLineClamp: 3,
                                                 WebkitBoxOrient: "vertical", overflow: "hidden"}}>
                                    {detail.Description}
                                </Typography>
                            )}
                            <Typography sx={{fontSize: 11, color: Grey7, paddingTop: "8px"}}>
                                {counts.requests} request{counts.requests === 1 ? "" : "s"} ·{" "}
                                {counts.offers} response{counts.offers === 1 ? "" : "s"} ·{" "}
                                {counts.plans} plan{counts.plans === 1 ? "" : "s"}
                            </Typography>
                        </Box>;
                    })}
                </Box>
            </Box>

            <Box sx={{display: "flex", flexDirection: "column", gap: "18px", minWidth: 0}}>
                {/* THE LANE. */}
                <Paper elevation={0} sx={{border: `1px solid ${Grey2}`, borderRadius: "8px",
                                          padding: "12px 16px"}}>
                    <Box sx={{display: "grid", gridTemplateColumns: "1.1fr 1fr 1fr", gap: "12px",
                              paddingBottom: "8px", borderBottom: `1px solid ${Grey2}`}}>
                        {["Quote request", "Quote response", "Build plan"].map(head => (
                            <Typography key={head} sx={{fontSize: 11.5, color: Grey7,
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.04em"}}>
                                {head} ›
                            </Typography>
                        ))}
                    </Box>
                    <Box sx={{display: "flex", alignItems: "center", gap: "10px",
                              padding: "10px 0 4px"}}>
                        <Typography sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase",
                                         letterSpacing: "0.04em", flex: 1}}>
                            {current ? `Connected pipeline for ${current.Name ?? current.ObjectID ?? "this product"}`
                                     : "No product selected"}
                        </Typography>
                        <Button size="small" variant="outlined"
                                disabled={busy || !takesRecords || !current}
                                sx={{textTransform: "none"}}
                                onClick={() => onNewRequest(current?.MemberUSID ?? null)}>
                            New quote request
                        </Button>
                    </Box>
                    {current && inLane.length === 0 && (
                        <Box sx={{padding: "28px 0", textAlign: "center"}}>
                            <Typography sx={{fontSize: 13, color: Grey7}}>
                                {stillLoading ? "Reading the requests…" : "No quote requests yet."}
                            </Typography>
                            {!stillLoading && takesRecords && (
                                <Typography sx={{fontSize: 12.5, color: Blue3, cursor: "pointer",
                                                 paddingTop: "6px"}}
                                            onClick={() => onNewRequest(current.MemberUSID)}>
                                    + Create the first quote request
                                </Typography>
                            )}
                        </Box>
                    )}
                    {inLane.map(chain => <ChainRow key={chain.request.MemberUSID} chain={chain}/>)}
                </Paper>

                {/* EVERYTHING FILED HERE THAT NO LANE CLAIMS. */}
                {(outside.length > 0 || loose.length > 0) && (
                    <Paper elevation={0} sx={{border: `1px solid #f1e3a6`, background: "#fffdf3",
                                              borderRadius: "8px", padding: "12px 16px"}}>
                        <Typography sx={{fontSize: 13, fontWeight: 700, color: Grey17}}>
                            Project records outside a product pipeline
                        </Typography>
                        <Typography sx={{fontSize: 11.5, color: Grey7, paddingBottom: "4px"}}>
                            Quote requests filed under no product stay here. Filing one under a
                            product moves its whole chain into that product's lane.
                        </Typography>
                        {outside.map(chain => <ChainRow key={chain.request.MemberUSID} chain={chain}/>)}
                        {loose.length > 0 && (
                            <Box sx={{paddingTop: "10px"}}>
                                <Typography sx={{fontSize: 11.5, color: Grey7, textTransform: "uppercase",
                                                 letterSpacing: "0.04em"}}>
                                    Filed here, reached by no request
                                </Typography>
                                {loose.map(one => (
                                    <Box key={one.MemberUSID} sx={{padding: "6px 0"}}>
                                        <RecordLine id={one.ObjectID} name={one.Name}
                                                    revisionUSID={one.visible ? one.RevisionUSID : null}/>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {takesRecords && (
                            <Typography sx={{fontSize: 12.5, color: Blue3, cursor: "pointer",
                                             paddingTop: "10px", textAlign: "center"}}
                                        onClick={() => onNewRequest(null)}>
                                + Add a quote request outside any pipeline
                            </Typography>
                        )}
                    </Paper>
                )}
            </Box>
        </Box>
    );
}
